import { ReceiptIcon } from "@phosphor-icons/react/dist/csr/Receipt";
import { useMemo } from "react";
import { Button } from "@volca/ui/components/ui/button";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@volca/ui/components/ui/empty";
import { ErrorState } from "@volca/ui/components/ui/error-state";
import { Stack } from "@volca/ui/components/ui/stack";
import {
  getEdgeFadeStyle,
  useScrollEdges,
} from "@volca/ui/hooks/use-scroll-edges";
import { emptyStateVariants } from "@volca/ui/lib/empty-state";
import {
  InvoiceGroupCardSkeleton,
  LoadMoreButton,
} from "@/accounts-payable/common";
import { useGetAutomatedInvoices } from "@/accounts-payable/query";
import { flattenPages } from "@/accounts-payable/utils";
import { AutomatedInvoiceCard } from "./automated-invoice-card";

export const ApAutomatedTab = ({
  businessId,
  onSelectInvoice,
}: {
  businessId: string | null;
  onSelectInvoice: (invoiceId: string) => void;
}) => {
  const {
    data,
    isLoading,
    isPending,
    isError,
    errorUpdatedAt,
    refetch,
    isFetching,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useGetAutomatedInvoices(businessId);

  const items = useMemo(() => flattenPages(data?.pages), [data]);
  const { setNode, edges } = useScrollEdges(true);

  const failedLoad = isError || (isFetching && !data && errorUpdatedAt > 0);
  if ((isPending || isLoading) && !failedLoad) {
    return (
      <Stack
        ref={setNode}
        className="h-full min-h-0 w-full overflow-y-auto"
        style={getEdgeFadeStyle(edges)}
      >
        <Stack align="start" gap={4} className="w-full px-px pt-4">
          <InvoiceGroupCardSkeleton rows={3} />
          <InvoiceGroupCardSkeleton rows={2} />
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack
      ref={setNode}
      className="h-full min-h-0 w-full overflow-y-auto"
      style={getEdgeFadeStyle(edges)}
    >
      {failedLoad && items.length === 0 ? (
        <ErrorState
          title="Couldn't load automatically handled invoices"
          description="Please try again."
          retrying={isFetching}
        >
          <Button
            variant="outline"
            size="sm"
            disabled={isFetching}
            aria-busy={isFetching || undefined}
            onClick={() => void refetch()}
          >
            Try again
          </Button>
        </ErrorState>
      ) : items.length > 0 ? (
        <Stack align="start" gap={4} className="w-full px-px pt-4">
          {items.map((item) => (
            <AutomatedInvoiceCard
              key={item.id}
              item={item}
              onOpenInvoice={() => onSelectInvoice(item.id)}
            />
          ))}
          {hasNextPage && (
            <Stack className="w-full" align="start" gap={4}>
              <LoadMoreButton
                onClick={() => fetchNextPage()}
                loading={isFetchingNextPage}
                disabled={isFetching}
              />
            </Stack>
          )}
        </Stack>
      ) : (
        // Serves both "nothing qualified yet" and "automation not enabled" —
        // the frontend has no signal to tell them apart.
        <Empty
          data-machine-host
          className={emptyStateVariants({ layout: "panel" })}
        >
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <ReceiptIcon aria-hidden />
            </EmptyMedia>
            <EmptyTitle>
              Your automatically handled invoices will show up here
            </EmptyTitle>
            <EmptyDescription>
              When an invoice can be handled end to end, it&apos;s approved and
              applied automatically. No review needed.
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      )}
    </Stack>
  );
};
